import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, AlertCircle, AlertTriangle, CheckCircle2, Clock, GitCompareArrows,
  Layers, RefreshCcw, Search, Shield, Upload, UserCheck, Building2,
} from 'lucide-react';
import { getTenantToday } from '../utils/dateUtils';

import { DateField } from './shared/DateField';

/* ════════════════════════════════════════════════════════════════
   Reconciliation Center — unified 3-way (IBOR vs Agent Bank vs Fund Admin)
   Source-agnostic workspace. The differentiator is the ATTRIBUTION column:
   not just *that* a number is off, but *which party owns the break*.
   ════════════════════════════════════════════════════════════════ */

/* ────────── helpers ────────── */
const cn = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(' ');

const fmtMoney = (v: number | null | undefined): string => {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

/* Par/quantity fields are notionals, not dollars — render without $ prefix */
const fmtQty = (v: number | null | undefined): string => {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const QTY_FIELDS = new Set<ReconField>(['PAR_QUANTITY']);
const fmtField = (field: ReconField, v: number | null | undefined): string =>
  QTY_FIELDS.has(field) ? fmtQty(v) : fmtMoney(v);

/* ────────── API types (mirror backend contract exactly) ────────── */
type ReconSource = 'FUND_ADMIN' | 'AGENT_BANK' | 'CUSTODIAN';
type ReconScope = 'PRIVATE_CREDIT' | 'LEVERAGED_LOAN' | 'ALL';

type ReconField =
  | 'PAR_QUANTITY' | 'FUNDED' | 'UNFUNDED' | 'ACCRUED_INTEREST'
  | 'COST' | 'BOOK_VALUE' | 'MARKET_VALUE';

type AttributedSource =
  | 'IBOR' | 'AGENT_BANK' | 'FUND_ADMIN' | 'CUSTODIAN' | 'ALL_DIFFER' | 'NONE';

type ReconCause =
  | 'ADMIN_NOT_BOOKED' | 'IBOR_MISSING_EVENT' | 'AGENT_TAPE_DISCREPANCY'
  | 'ALL_THREE_DIFFER' | 'TWO_WAY_MISMATCH';

type ReconSeverity = 'HIGH' | 'MEDIUM' | 'LOW';
type ReconBreakStatus = 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'SUPPRESSED';
type RunStatus = string;

/** POST /api/recon/files response */
interface StageFileResult {
  reconRunId: number;
  source: ReconSource;
  rowsParsed: number;
  rowsStaged: number;
  rowsRejected: number;
  columnMapping: Record<string, string>;
  status: string;
}

/** GET /api/recon/runs row */
interface ReconRun {
  id: number;
  businessDate: string;
  scope: ReconScope;
  sources: ReconSource[];
  status: RunStatus;
  positionCount: number;
  breakCount: number;
  reconciledCount?: number;
  matchRate?: number;
}

/** Severity / cause / status rollups returned with GET /api/recon/runs/{id} */
interface ReconRunSummary extends ReconRun {
  countsBySeverity?: Partial<Record<ReconSeverity, number>>;
  countsByCause?: Partial<Record<ReconCause, number>>;
  countsByStatus?: Partial<Record<ReconBreakStatus, number>>;
}

const RECON_SOURCE_VALUES = new Set<string>(['FUND_ADMIN', 'AGENT_BANK', 'CUSTODIAN']);

function normalizeSources(raw: unknown): ReconSource[] {
  const values = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? raw.split(',')
      : [];

  return values
    .map(value => String(value).trim())
    .filter((value): value is ReconSource => RECON_SOURCE_VALUES.has(value));
}

function normalizeRun(raw: any): ReconRun {
  return {
    ...raw,
    scope: (raw?.scope ?? 'ALL') as ReconScope,
    sources: normalizeSources(raw?.sources),
    positionCount: raw?.positionCount ?? 0,
    breakCount: raw?.breakCount ?? 0,
  };
}

/**
 * The backend `ReconRunSummary` nests the run fields under `run`, names the
 * rollups `breaksBy*` (not `countsBy*`), and does not emit `reconciledCount` /
 * `matchRate`. This screen reads them flat — without normalization every KPI
 * (including the cause counts) reads `undefined` and renders 0 even when the
 * engine persisted real breaks. Flatten + alias + derive here at the boundary.
 */
function normalizeRunSummary(raw: any): ReconRunSummary | null {
  if (!raw) return null;
  const run = raw.run ?? raw;
  const breakCount = raw.breakCount ?? run.breakCount ?? 0;
  const positionCount = run.positionCount ?? raw.positionCount ?? 0;
  const reconciledCount = raw.reconciledCount ?? run.reconciledCount
    ?? Math.max(0, positionCount - breakCount);
  const matchRate = raw.matchRate ?? run.matchRate
    ?? (positionCount > 0 ? (reconciledCount / positionCount) * 100 : 0);
  return {
    ...normalizeRun({ ...run, breakCount, positionCount }),
    breakCount,
    positionCount,
    reconciledCount,
    matchRate,
    countsBySeverity: raw.countsBySeverity ?? raw.breaksBySeverity ?? {},
    countsByCause: raw.countsByCause ?? raw.breaksByCause ?? {},
    countsByStatus: raw.countsByStatus ?? raw.breaksByStatus ?? {},
  };
}

/** GET /api/recon/runs/{id}/breaks row */
interface ReconFieldBreak {
  id: number;
  positionKey: string;
  borrowerName: string;
  loanSource: string;
  field: ReconField;
  iborValue: number | null;
  agentValue: number | null;
  adminValue: number | null;
  custodianValue: number | null;
  maxAbsDiff: number | null;
  attributedSource: AttributedSource;
  cause: ReconCause;
  severity: ReconSeverity;
  status: ReconBreakStatus;
  recommendedAction: string | null;
  assignedTo: string | null;
  resolvedBy: string | null;
  resolutionNotes: string | null;
}

/* ────────── source / scope config ────────── */
const SOURCE_META: Record<ReconSource, { label: string; short: string; icon: React.ReactNode }> = {
  CUSTODIAN: { label: 'Custodian / SWIFT', short: 'Custodian', icon: <Building2 className="w-3.5 h-3.5" /> },
  FUND_ADMIN: { label: 'Fund Admin', short: 'Fund Admin', icon: <Shield className="w-3.5 h-3.5" /> },
  AGENT_BANK: { label: 'Agent Bank', short: 'Agent Bank', icon: <Layers className="w-3.5 h-3.5" /> },
};
const SOURCE_ORDER: ReconSource[] = ['CUSTODIAN', 'FUND_ADMIN', 'AGENT_BANK'];

const SCOPE_META: Record<ReconScope, string> = {
  ALL: 'All',
  PRIVATE_CREDIT: 'Private Credit',
  LEVERAGED_LOAN: 'Leveraged Loan',
};
const SCOPE_ORDER: ReconScope[] = ['ALL', 'PRIVATE_CREDIT', 'LEVERAGED_LOAN'];

const FIELD_LABEL: Record<ReconField, string> = {
  PAR_QUANTITY: 'Par / Quantity',
  FUNDED: 'Funded',
  UNFUNDED: 'Unfunded',
  ACCRUED_INTEREST: 'Accrued Interest',
  COST: 'Cost',
  BOOK_VALUE: 'Book Value',
  MARKET_VALUE: 'Market Value',
};
const FIELD_ORDER: ReconField[] = [
  'FUNDED', 'UNFUNDED', 'PAR_QUANTITY', 'ACCRUED_INTEREST', 'COST', 'BOOK_VALUE', 'MARKET_VALUE',
];

/* The marquee mapping: cause → operator-facing meaning + chip styling. */
const CAUSE_META: Record<ReconCause, { label: string; tip: string; cls: string }> = {
  ADMIN_NOT_BOOKED: {
    label: "Admin hasn't booked",
    tip: 'Fund administrator has not yet booked this event — chase the admin.',
    cls: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  },
  IBOR_MISSING_EVENT: {
    label: 'Post the missing event',
    tip: 'Your book is missing a lifecycle event — post it via pre-clear.',
    cls: 'bg-blue-500/10 text-blue-300 border-blue-500/25',
  },
  AGENT_TAPE_DISCREPANCY: {
    label: 'Query agent bank',
    tip: 'Agent-bank / WSO tape disagrees with the other legs — query the agent bank.',
    cls: 'bg-violet-500/10 text-violet-300 border-violet-500/25',
  },
  ALL_THREE_DIFFER: {
    label: 'All three differ',
    tip: 'Every leg reports a different value — manual investigation required.',
    cls: 'bg-red-500/10 text-red-300 border-red-500/25',
  },
  TWO_WAY_MISMATCH: {
    label: 'Needs 3rd source',
    tip: 'Only two legs present — cannot attribute until the third source lands.',
    cls: 'bg-slate-500/10 text-slate-300 border-slate-500/25',
  },
};

const SEVERITY_CLS: Record<ReconSeverity, string> = {
  HIGH: 'bg-red-500/10 text-red-400 border-red-500/20',
  MEDIUM: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  LOW: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
};

const statusBadge = (s: ReconBreakStatus | RunStatus): string => {
  if (s === 'RESOLVED' || s === 'COMPLETE' || s === 'COMPLETED' || s === 'RECONCILED') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
  if (s === 'OPEN') return 'bg-red-500/10 text-red-400 border-red-500/20';
  if (s === 'INVESTIGATING' || s === 'RUNNING' || s === 'STAGED') return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  if (s === 'SUPPRESSED') return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
  return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
};
const statusIcon = (s: ReconBreakStatus | RunStatus): React.ReactNode => {
  if (s === 'RESOLVED' || s === 'COMPLETE' || s === 'COMPLETED' || s === 'RECONCILED') return <CheckCircle2 className="w-3 h-3" />;
  if (s === 'OPEN') return <AlertTriangle className="w-3 h-3" />;
  return <Clock className="w-3 h-3" />;
};

/* ────────── sub-components ────────── */
const Kpi: React.FC<{
  label: string; value: string | number; accent?: string; sub?: string; borderColor?: string;
}> = ({ label, value, accent = 'text-white', sub, borderColor }) => (
  <div
    className={cn('bg-slate-900/40 border border-white/5 rounded-xl p-5', borderColor && 'border-l-4')}
    style={borderColor ? { borderLeftColor: borderColor } : undefined}
  >
    <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-2">{label}</div>
    <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

type TabKey = 'breaks' | 'stage' | 'runs';

/* ════════════════════════════════════════════════════════════════ */
const ReconciliationCenterScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('breaks');

  /* ── global filters ── */
  const [sourceFilter, setSourceFilter] = useState<'ALL' | ReconSource>('ALL');
  const [scopeFilter, setScopeFilter] = useState<ReconScope>('ALL');

  /* ── runs / selected run ── */
  const [runs, setRuns] = useState<ReconRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [runSummary, setRunSummary] = useState<ReconRunSummary | null>(null);

  /* ── breaks ── */
  const [breaks, setBreaks] = useState<ReconFieldBreak[]>([]);
  const [loadingBreaks, setLoadingBreaks] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ── break table filters ── */
  const [statusFilter, setStatusFilter] = useState<'ALL' | ReconBreakStatus>('ALL');
  const [fieldFilter, setFieldFilter] = useState<'ALL' | ReconField>('ALL');
  const [causeFilter, setCauseFilter] = useState<'ALL' | ReconCause>('ALL');
  const [searchText, setSearchText] = useState('');

  /* ── staging (upload) ── */
  const [stageSource, setStageSource] = useState<ReconSource>('AGENT_BANK');
  const [stageScope, setStageScope] = useState<ReconScope>('ALL');
  const [stageDate, setStageDate] = useState(getTenantToday());
  const [stageFile, setStageFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isStaging, setIsStaging] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  /* the open run we are staging sources into before reconciling */
  const [stagingRunId, setStagingRunId] = useState<number | null>(null);
  const [stagedResults, setStagedResults] = useState<StageFileResult[]>([]);

  /* ── assign / resolve modal ── */
  const [actionModal, setActionModal] = useState<{ mode: 'assign' | 'resolve' | null; breakId: number | null }>({ mode: null, breakId: null });
  const [actorName, setActorName] = useState('');
  const [actionNotes, setActionNotes] = useState('');

  /* ── data fetchers ── */
  const fetchRuns = useCallback(async () => {
    try {
      const res = await axios.get('/api/recon/runs');
      const list: ReconRun[] = Array.isArray(res.data) ? res.data.map(normalizeRun) : [];
      setRuns(list);
      setSelectedRunId(prev => (prev != null ? prev : list[0]?.id ?? null));
    } catch (err: any) {
      if (err.response?.status === 429) setError('Rate limit exceeded — wait a moment.');
      else setError('Failed to load reconciliation runs.');
    }
  }, []);

  const fetchRunSummary = useCallback(async (runId: number) => {
    try {
      const res = await axios.get(`/api/recon/runs/${runId}`);
      setRunSummary(normalizeRunSummary(res.data));
    } catch {
      setRunSummary(null);
    }
  }, []);

  const fetchBreaks = useCallback(async (runId: number) => {
    setLoadingBreaks(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (sourceFilter !== 'ALL') params.append('source', sourceFilter);
      if (fieldFilter !== 'ALL') params.append('field', fieldFilter);
      const qs = params.toString();
      const res = await axios.get(`/api/recon/runs/${runId}/breaks${qs ? `?${qs}` : ''}`);
      setBreaks(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      if (err.response?.status === 429) setError('Rate limit exceeded — wait a moment.');
      else setError('Failed to load breaks.');
      setBreaks([]);
    } finally {
      setLoadingBreaks(false);
    }
  }, [statusFilter, sourceFilter, fieldFilter]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  useEffect(() => {
    if (selectedRunId == null) { setBreaks([]); setRunSummary(null); return; }
    fetchRunSummary(selectedRunId);
    fetchBreaks(selectedRunId);
  }, [selectedRunId, fetchRunSummary, fetchBreaks]);

  /* ── staging handlers ── */
  const handleStageFile = async () => {
    if (!stageFile) return;
    setIsStaging(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', stageFile);
      formData.append('source', stageSource);
      formData.append('businessDate', stageDate);
      formData.append('scope', stageScope);
      if (stagingRunId != null) formData.append('reconRunId', String(stagingRunId));
      const res = await axios.post<StageFileResult>('/api/recon/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = res.data;
      setStagingRunId(result.reconRunId);
      setStagedResults(prev => [...prev.filter(r => r.source !== result.source), result]);
      setStageFile(null);
      fetchRuns();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'File staging failed.');
    } finally {
      setIsStaging(false);
    }
  };

  const handleReconcile = async () => {
    if (stagingRunId == null) return;
    setIsReconciling(true);
    setError(null);
    try {
      await axios.post(`/api/recon/runs/${stagingRunId}/reconcile`);
      const runId = stagingRunId;
      setStagedResults([]);
      setStagingRunId(null);
      await fetchRuns();
      setSelectedRunId(runId);
      setActiveTab('breaks');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Reconciliation run failed.');
    } finally {
      setIsReconciling(false);
    }
  };

  const handleAssign = async () => {
    if (actionModal.breakId == null || !actorName.trim()) return;
    try {
      await axios.put(`/api/recon/breaks/${actionModal.breakId}/assign`, { assignedTo: actorName.trim() });
      closeActionModal();
      if (selectedRunId != null) fetchBreaks(selectedRunId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to assign break.');
    }
  };

  const handleResolve = async () => {
    if (actionModal.breakId == null || !actorName.trim()) return;
    try {
      await axios.put(`/api/recon/breaks/${actionModal.breakId}/resolve`, { resolvedBy: actorName.trim(), notes: actionNotes.trim() });
      closeActionModal();
      if (selectedRunId != null) { fetchBreaks(selectedRunId); fetchRunSummary(selectedRunId); }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resolve break.');
    }
  };

  const closeActionModal = () => { setActionModal({ mode: null, breakId: null }); setActorName(''); setActionNotes(''); };

  /* ── derived: KPIs from the selected run summary (DB-sourced, no faking) ── */
  const selectedRun = useMemo(
    () => runs.find(r => r.id === selectedRunId) ?? null,
    [runs, selectedRunId],
  );

  const positionCount = runSummary?.positionCount ?? selectedRun?.positionCount ?? 0;
  const breakCount = runSummary?.breakCount ?? selectedRun?.breakCount ?? 0;
  const reconciledCount = runSummary?.reconciledCount
    ?? selectedRun?.reconciledCount
    ?? Math.max(positionCount - breakCount, 0);
  const matchRate = runSummary?.matchRate
    ?? selectedRun?.matchRate
    ?? (positionCount > 0 ? (reconciledCount / positionCount) * 100 : 0);

  const causeCounts = runSummary?.countsByCause ?? {};
  const adminNotBooked = causeCounts.ADMIN_NOT_BOOKED ?? 0;
  const iborMissing = causeCounts.IBOR_MISSING_EVENT ?? 0;
  const agentTape = causeCounts.AGENT_TAPE_DISCREPANCY ?? 0;

  /* ── client-side break filtering (cause + free text; status/source/field are server-side) ── */
  const visibleBreaks = useMemo(() => {
    return breaks.filter(b => {
      if (causeFilter !== 'ALL' && b.cause !== causeFilter) return false;
      if (scopeFilter !== 'ALL') {
        const s = (b.loanSource || '').toUpperCase();
        if (scopeFilter === 'PRIVATE_CREDIT' && !s.includes('PRIVATE') && !s.includes('PC')) return false;
        if (scopeFilter === 'LEVERAGED_LOAN' && !s.includes('LEVERAGED') && !s.includes('LL') && !s.includes('SYNDICAT')) return false;
      }
      if (searchText.trim()) {
        const q = searchText.trim().toLowerCase();
        if (!(b.borrowerName || '').toLowerCase().includes(q) && !(b.positionKey || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [breaks, causeFilter, scopeFilter, searchText]);

  /* highlight the differing leg(s) for a given break */
  const isOutlier = (b: ReconFieldBreak, leg: AttributedSource): boolean => {
    if (b.attributedSource === 'ALL_DIFFER') return true;
    return b.attributedSource === leg;
  };

  /* ════════════════════════════════════════════════════════════════ */
  return (
    <div className="px-6 py-6 text-slate-100">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
              <GitCompareArrows className="w-5 h-5 text-blue-400" />
              Reconciliation Center
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              IBOR vs Agent Bank vs Fund Admin — one attributed exception workflow
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Run selector */}
            <select
              value={selectedRunId ?? ''}
              onChange={e => setSelectedRunId(e.target.value ? Number(e.target.value) : null)}
              className="bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-blue-500/50 appearance-none max-w-[260px]"
            >
              {runs.length === 0 && <option value="">No runs yet</option>}
              {runs.map(r => (
                <option key={r.id} value={r.id}>
                  Run #{r.id} · {r.businessDate} · {SCOPE_META[r.scope] ?? r.scope} · {r.breakCount} breaks
                </option>
              ))}
            </select>
            {/* Tab bar */}
            <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
              {([['breaks', 'Breaks'], ['stage', 'Stage & Run'], ['runs', 'Runs']] as [TabKey, string][]).map(([tab, label]) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'px-4 py-1.5 rounded-lg text-xs font-bold transition-all',
                    activeTab === tab ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => { fetchRuns(); if (selectedRunId != null) { fetchBreaks(selectedRunId); fetchRunSummary(selectedRunId); } }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
            >
              <RefreshCcw className={cn('w-4 h-4', loadingBreaks && 'animate-spin')} /> Refresh
            </button>
          </div>
        </div>

        {/* Source + Scope filter pills */}
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mr-1">Source</span>
            {(['ALL', ...SOURCE_ORDER] as ('ALL' | ReconSource)[]).map(s => (
              <button
                key={s}
                onClick={() => setSourceFilter(s)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors border',
                  sourceFilter === s
                    ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                    : 'bg-slate-800/50 text-slate-500 border-white/5 hover:text-slate-300',
                )}
              >
                {s !== 'ALL' && SOURCE_META[s].icon}
                {s === 'ALL' ? 'All' : SOURCE_META[s].short}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mr-1">Scope</span>
            {SCOPE_ORDER.map(s => (
              <button
                key={s}
                onClick={() => setScopeFilter(s)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors border',
                  scopeFilter === s
                    ? 'bg-violet-500/20 text-violet-300 border-violet-500/30'
                    : 'bg-slate-800/50 text-slate-500 border-white/5 hover:text-slate-300',
                )}
              >
                {SCOPE_META[s]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-between gap-3 text-red-400 font-bold mb-6"
        >
          <div className="flex items-center gap-3"><AlertCircle className="w-5 h-5" />{error}</div>
          <button onClick={() => setError(null)} className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 transition-colors">Dismiss</button>
        </motion.div>
      )}

      {/* KPI strip — attribution-first */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <Kpi label="Positions" value={positionCount} sub="In selected run" borderColor="#3b82f6" />
        <Kpi label="Reconciled" value={reconciledCount} accent="text-emerald-400" sub={`${matchRate.toFixed(1)}% match`} borderColor="#10b981" />
        <Kpi label="Breaks" value={breakCount} accent={breakCount > 0 ? 'text-red-400' : 'text-emerald-400'} sub="Field-level" borderColor="#ef4444" />
        <Kpi label="Admin not booked" value={adminNotBooked} accent="text-amber-400" sub="Chase the admin" borderColor="#f59e0b" />
        <Kpi label="IBOR missing event" value={iborMissing} accent="text-blue-400" sub="Post via pre-clear" borderColor="#3b82f6" />
        <Kpi label="Agent tape" value={agentTape} accent="text-violet-400" sub="Query agent bank" borderColor="#8b5cf6" />
        <Kpi label="Health" value={`${matchRate.toFixed(1)}%`} accent={matchRate >= 95 ? 'text-emerald-400' : 'text-amber-400'} sub="Match rate" borderColor="#06b6d4" />
      </div>

      {/* ══════════════ BREAKS TAB (the marquee) ══════════════ */}
      {activeTab === 'breaks' && (
        <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-sm font-semibold text-white">
                {selectedRunId ? `Field Breaks — Run #${selectedRunId}` : 'Field Breaks'}
              </span>
              <span className="text-xs text-slate-500 ml-2">{visibleBreaks.length} shown</span>
            </div>
            {/* table filters */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <input
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder="Borrower / key…"
                  className="bg-slate-950 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500/50 w-44"
                />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}
                className="bg-slate-950 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-blue-500/50 appearance-none">
                <option value="ALL">All statuses</option>
                {(['OPEN', 'INVESTIGATING', 'RESOLVED', 'SUPPRESSED'] as ReconBreakStatus[]).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={fieldFilter} onChange={e => setFieldFilter(e.target.value as any)}
                className="bg-slate-950 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-blue-500/50 appearance-none">
                <option value="ALL">All fields</option>
                {FIELD_ORDER.map(f => <option key={f} value={f}>{FIELD_LABEL[f]}</option>)}
              </select>
              <select value={causeFilter} onChange={e => setCauseFilter(e.target.value as any)}
                className="bg-slate-950 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-blue-500/50 appearance-none">
                <option value="ALL">All attribution</option>
                {(Object.keys(CAUSE_META) as ReconCause[]).map(c => <option key={c} value={c}>{CAUSE_META[c].label}</option>)}
              </select>
            </div>
          </div>

          {loadingBreaks ? (
            <div className="flex items-center justify-center py-20 text-slate-500 text-sm">Loading breaks…</div>
          ) : selectedRunId == null ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500 text-sm gap-2">
              <GitCompareArrows className="w-8 h-8 text-slate-600" />
              No run selected. Stage source files and run a reconciliation to see attributed breaks.
            </div>
          ) : visibleBreaks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500 text-sm gap-2">
              <CheckCircle2 className="w-8 h-8 text-emerald-500/60" />
              No breaks match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-900/95 backdrop-blur z-10">
                  <tr className="border-b border-white/5">
                    <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-4">Borrower / Position</th>
                    <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-4">Field</th>
                    {/* three legs side-by-side */}
                    <th className="text-right text-[10px] font-semibold text-violet-300/80 uppercase tracking-widest py-3 px-3 border-l border-white/5">Agent Bank</th>
                    <th className="text-right text-[10px] font-semibold text-blue-300/80 uppercase tracking-widest py-3 px-3">AxiomFlow (IBOR)</th>
                    <th className="text-right text-[10px] font-semibold text-amber-300/80 uppercase tracking-widest py-3 px-3">Fund Admin</th>
                    <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-3 border-r border-white/5">Custodian</th>
                    <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-4">Attribution — who owns it</th>
                    <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-3">Sev</th>
                    <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-4">Recommended action</th>
                    <th className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-3">Status</th>
                    <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {visibleBreaks.map(b => {
                    const cause = CAUSE_META[b.cause] ?? CAUSE_META.TWO_WAY_MISMATCH;
                    const legCell = (leg: AttributedSource, value: number | null) => {
                      const outlier = isOutlier(b, leg);
                      return (
                        <td className={cn(
                          'py-2.5 px-3 text-right font-mono text-[13px]',
                          value == null ? 'text-slate-600 italic'
                            : outlier ? 'text-red-300 font-bold bg-red-500/[0.06]'
                              : 'text-slate-200',
                        )}>
                          {fmtField(b.field, value)}
                        </td>
                      );
                    };
                    return (
                      <tr key={b.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                        <td className="py-2.5 px-4">
                          <div className="text-sm font-medium text-slate-200 truncate max-w-[200px]">{b.borrowerName || b.positionKey}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{b.positionKey}{b.loanSource ? ` · ${b.loanSource}` : ''}</div>
                        </td>
                        <td className="py-2.5 px-4">
                          <span className="text-xs font-semibold text-slate-300">{FIELD_LABEL[b.field] ?? b.field}</span>
                        </td>
                        {legCell('AGENT_BANK', b.agentValue)}
                        {legCell('IBOR', b.iborValue)}
                        {legCell('FUND_ADMIN', b.adminValue)}
                        {legCell('CUSTODIAN', b.custodianValue)}
                        {/* ATTRIBUTION — the whole pitch */}
                        <td className="py-2.5 px-4">
                          <span
                            title={cause.tip}
                            className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border', cause.cls)}
                          >
                            {cause.label}
                          </span>
                          {b.maxAbsDiff != null && (
                            <div className="text-[10px] text-slate-500 mt-1 font-mono">Δ {fmtField(b.field, b.maxAbsDiff)}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border', SEVERITY_CLS[b.severity] ?? SEVERITY_CLS.LOW)}>
                            {b.severity}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 max-w-[220px]">
                          <div className="text-[11px] text-slate-300 leading-snug line-clamp-2" title={b.recommendedAction ?? undefined}>
                            {b.recommendedAction || <span className="text-slate-600">—</span>}
                          </div>
                          {b.assignedTo && <div className="text-[10px] text-blue-400/80 mt-1">Assigned: {b.assignedTo}</div>}
                          {b.resolvedBy && <div className="text-[10px] text-emerald-400/80 mt-1">Resolved by: {b.resolvedBy}</div>}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border', statusBadge(b.status))}>
                            {statusIcon(b.status)}{b.status}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-right whitespace-nowrap">
                          {b.status !== 'RESOLVED' && b.status !== 'SUPPRESSED' && (
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                onClick={() => { setActionModal({ mode: 'assign', breakId: b.id }); setActorName(b.assignedTo ?? ''); }}
                                className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-bold rounded-lg transition-all border border-white/5"
                              >
                                Assign
                              </button>
                              <button
                                onClick={() => { setActionModal({ mode: 'resolve', breakId: b.id }); }}
                                className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold rounded-lg transition-all"
                              >
                                Resolve
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════ STAGE & RUN TAB ══════════════ */}
      {activeTab === 'stage' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* upload panel */}
          <div className="lg:col-span-2 bg-slate-900/40 border border-white/5 rounded-xl p-8">
            <h3 className="text-lg font-bold text-white mb-2">Stage a Source File</h3>
            <p className="text-xs text-slate-500 mb-6">
              Upload one source's file at a time into a single recon run. Stage agent-bank tape, fund-admin file,
              and custodian statement, then run the reconciliation once all the legs you need are present.
            </p>

            {/* Drop zone */}
            <div
              className={cn(
                'border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer',
                isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-white/10 hover:border-blue-500/50 hover:bg-white/5',
              )}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={e => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) setStageFile(f); }}
              onClick={() => document.getElementById('reconFileInput')?.click()}
            >
              <Upload className="w-10 h-10 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-300 font-medium">{stageFile ? stageFile.name : 'Drag and drop a source file, or click to browse'}</p>
              <p className="text-xs text-slate-500 mt-2">Accepts: .csv, .xlsx, .xls, .txt</p>
              <input id="reconFileInput" type="file" className="hidden" accept=".csv,.xlsx,.xls,.txt"
                onChange={e => setStageFile(e.target.files?.[0] || null)} />
            </div>

            {/* form fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-7">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Source</label>
                <select value={stageSource} onChange={e => setStageSource(e.target.value as ReconSource)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50 appearance-none">
                  {SOURCE_ORDER.map(s => <option key={s} value={s}>{SOURCE_META[s].label}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Scope</label>
                <select value={stageScope} onChange={e => setStageScope(e.target.value as ReconScope)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50 appearance-none">
                  {SCOPE_ORDER.map(s => <option key={s} value={s}>{SCOPE_META[s]}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Business Date</label>
                <DateField
                  value={stageDate ?? ''}
                  className="w-full"
                  onChange={iso => setStageDate(iso)}
                />
              </div>
            </div>

            <button onClick={handleStageFile} disabled={isStaging || !stageFile}
              className="w-full mt-7 py-3.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2">
              {isStaging ? <RefreshCcw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              Stage {SOURCE_META[stageSource].short} File
            </button>
          </div>

          {/* staged-sources summary + run button */}
          <div className="space-y-6">
            <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
              <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                <Layers className="w-4 h-4 text-blue-400" />
                Sources staged {stagingRunId != null && <span className="text-[11px] font-normal text-slate-500">into Run #{stagingRunId}</span>}
              </h4>
              {stagedResults.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-4 text-center">No sources staged yet</div>
              ) : (
                <div className="space-y-2.5">
                  {SOURCE_ORDER.map(src => {
                    const r = stagedResults.find(x => x.source === src);
                    return (
                      <div key={src} className={cn('flex items-center justify-between p-3 rounded-lg border',
                        r ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-slate-800/30 border-white/5')}>
                        <div className="flex items-center gap-2">
                          {r ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Clock className="w-3.5 h-3.5 text-slate-600" />}
                          <span className={cn('text-xs font-semibold', r ? 'text-slate-200' : 'text-slate-500')}>{SOURCE_META[src].label}</span>
                        </div>
                        {r ? (
                          <div className="text-right">
                            <div className="text-[11px] font-mono text-emerald-400">{r.rowsStaged} staged</div>
                            {r.rowsRejected > 0 && <div className="text-[10px] font-mono text-amber-400">{r.rowsRejected} rejected</div>}
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-600 uppercase">not staged</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                onClick={handleReconcile}
                disabled={isReconciling || stagingRunId == null || stagedResults.length === 0}
                className="w-full mt-4 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                {isReconciling ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                Run Reconciliation
              </button>
            </div>

            {/* last staged column mapping (transparency) */}
            {stagedResults.length > 0 && (
              <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
                <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  Column Mapping
                </h4>
                {(() => {
                  const last = stagedResults[stagedResults.length - 1];
                  const entries = Object.entries(last.columnMapping ?? {});
                  return (
                    <>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">{SOURCE_META[last.source].label} · {last.rowsParsed} parsed</div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {entries.length === 0 ? (
                          <div className="text-xs text-slate-600 italic">No mapping reported</div>
                        ) : entries.map(([src, dest]) => (
                          <div key={src} className="flex items-center justify-between text-[11px]">
                            <span className="text-slate-500 font-mono truncate max-w-[120px]">{src}</span>
                            <span className="text-slate-600 px-2">→</span>
                            <span className="text-slate-300 font-semibold truncate max-w-[120px]">{dest}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ RUNS TAB ══════════════ */}
      {activeTab === 'runs' && (
        <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-semibold text-white">Reconciliation Runs</span>
            <span className="text-xs text-slate-500 ml-2">{runs.length} runs</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                  <th className="py-3 px-4">Run</th>
                  <th className="py-3 px-4">Business Date</th>
                  <th className="py-3 px-4">Scope</th>
                  <th className="py-3 px-4">Sources</th>
                  <th className="py-3 px-4 text-right">Positions</th>
                  <th className="py-3 px-4 text-right">Breaks</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {runs.map(r => (
                  <tr key={r.id}
                    className={cn('border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors',
                      r.id === selectedRunId && 'bg-blue-500/[0.05]')}>
                    <td className="py-3 px-4 text-slate-400">#{r.id}</td>
                    <td className="py-3 px-4 text-slate-300">{r.businessDate}</td>
                    <td className="py-3 px-4 text-slate-300">{SCOPE_META[r.scope] ?? r.scope}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {normalizeSources(r.sources).map(s => (
                          <span key={s} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-800/60 text-slate-300 border border-white/5">
                            {SOURCE_META[s]?.icon}{SOURCE_META[s]?.short ?? s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right text-slate-300 font-mono">{r.positionCount}</td>
                    <td className={cn('py-3 px-4 text-right font-bold font-mono', r.breakCount > 0 ? 'text-red-400' : 'text-emerald-400')}>{r.breakCount}</td>
                    <td className="py-3 px-4">
                      <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border', statusBadge(r.status))}>
                        {statusIcon(r.status)}{r.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => { setSelectedRunId(r.id); setActiveTab('breaks'); }}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-lg transition-all border border-white/5">
                        View Breaks
                      </button>
                    </td>
                  </tr>
                ))}
                {runs.length === 0 && (
                  <tr><td colSpan={8} className="py-12 text-center text-slate-500 italic">No reconciliation runs yet — stage source files to begin.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════ ASSIGN / RESOLVE MODAL ══════════════ */}
      <AnimatePresence>
        {actionModal.mode && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={closeActionModal} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-3xl shadow-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                {actionModal.mode === 'assign' ? <UserCheck className="w-5 h-5 text-blue-400" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
                {actionModal.mode === 'assign' ? 'Assign Break' : 'Resolve Break'} #{actionModal.breakId}
              </h3>
              <p className="text-xs text-slate-500 mb-6">
                {actionModal.mode === 'assign' ? 'Route this break to an analyst.' : 'Record who resolved this break and how.'}
              </p>
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    {actionModal.mode === 'assign' ? 'Assign to' : 'Resolved by'}
                  </label>
                  <input value={actorName} onChange={e => setActorName(e.target.value)}
                    className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50"
                    placeholder="Analyst name or username" />
                </div>
                {actionModal.mode === 'resolve' && (
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Resolution Notes</label>
                    <textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)}
                      className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 min-h-[120px]"
                      placeholder="e.g., Admin confirmed drawdown booked T+1; agent tape refreshed." />
                  </div>
                )}
                <div className="flex gap-4 pt-2">
                  <button onClick={closeActionModal}
                    className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all">Cancel</button>
                  <button onClick={actionModal.mode === 'assign' ? handleAssign : handleResolve} disabled={!actorName.trim()}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20">
                    {actionModal.mode === 'assign' ? 'Assign' : 'Save Resolution'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReconciliationCenterScreen;
